import React, { useState } from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Download, Mail, Phone } from 'lucide-react';

/**
 * LinkSelector - A reusable component for selecting internal pages, external URLs, anchors, downloads, emails or phone numbers
 */
const LinkSelector = ({ 
  value = '', 
  onChange, 
  pages = [], 
  anchors = [],  // Array of { id: 'anchor-id', name: 'Display Name', pageSlug: 'page-slug' }
  placeholder = 'https://example.com',
  label = 'Link',
  showAnchor = true,
  showDownload = true,
  testId = 'link-selector'
}) => {
  // Determine type from value
  const getTypeFromValue = (val) => {
    if (!val) return 'none';
    if (val.startsWith('mailto:')) return 'email';
    if (val.startsWith('tel:')) return 'phone';
    if (val.startsWith('download:')) return 'download';
    if (val.startsWith('http://') || val.startsWith('https://')) return 'external';
    if (val.startsWith('#')) return 'anchor';
    return 'internal';
  };

  // Track selected type independently - initialized from value but user-controlled after
  const [userSelectedType, setUserSelectedType] = useState(null);
  
  // The actual type to display: user selection takes priority, otherwise derive from value
  const selectedType = userSelectedType !== null ? userSelectedType : getTypeFromValue(value);
  
  // Helper to strip all download: prefixes
  const stripDownloadPrefix = (url) => {
    if (!url) return '';
    let clean = url;
    while (clean.startsWith('download:')) {
      clean = clean.substring(9);
    }
    return clean;
  };

  // Local state for inputs
  const [localExternal, setLocalExternal] = useState(value?.startsWith('http') ? value : '');
  const [localAnchor, setLocalAnchor] = useState(value?.startsWith('#') ? value.substring(1) : '');
  const [localDownload, setLocalDownload] = useState(stripDownloadPrefix(value?.startsWith('download:') ? value : ''));
  const [localEmail, setLocalEmail] = useState(value?.startsWith('mailto:') ? value.substring(7) : '');
  const [localPhone, setLocalPhone] = useState(value?.startsWith('tel:') ? value.substring(4) : '');

  // Get current page slug from value
  const getCurrentPage = () => {
    if (value && !value.startsWith('http') && !value.startsWith('#') && !value.startsWith('download:')) {
      return value.startsWith('/') ? value.substring(1) : value;
    }
    return '_none_';
  };

  // Get current anchor from value
  const getCurrentAnchor = () => {
    if (value && value.startsWith('#')) {
      return value.substring(1);
    }
    return '_none_';
  };

  // Get current download URL from value
  const getCurrentDownload = () => {
    if (value && value.startsWith('download:')) {
      return stripDownloadPrefix(value);
    }
    return '';
  };

  const handleTypeChange = (newType) => {
    setUserSelectedType(newType);
    
    if (newType === 'none') {
      onChange('');
      setLocalExternal('');
      setLocalAnchor('');
      setLocalDownload('');
      setLocalEmail('');
      setLocalPhone('');
    }
    // For other types, keep existing value until user changes it
  };

  const handlePageSelect = (slug) => {
    if (slug && slug !== '_none_') {
      onChange(`/${slug}`);
    } else {
      onChange('');
    }
  };

  const handleAnchorSelect = (anchorId) => {
    if (anchorId && anchorId !== '_none_') {
      // Find the anchor to get its page slug
      const selectedAnchor = anchors.find(a => a.id === anchorId);
      // Store as "/pageSlug#anchorId" if anchor has a page, otherwise just "#anchorId"
      const linkValue = selectedAnchor?.pageSlug 
        ? `/${selectedAnchor.pageSlug}#${anchorId}`
        : `#${anchorId}`;
      onChange(linkValue);
      setLocalAnchor(anchorId);
    } else {
      onChange('');
      setLocalAnchor('');
    }
  };

  const handleExternalChange = (url) => {
    setLocalExternal(url);
    onChange(url);
  };

  const handleAnchorInputChange = (anchor) => {
    const clean = anchor.startsWith('#') ? anchor.substring(1) : anchor;
    setLocalAnchor(clean);
    onChange(clean ? `#${clean}` : '');
  };

  const handleDownloadChange = (url) => {
    // Strip any existing "download:" prefix to prevent stacking (download:download:download:...)
    const cleanUrl = stripDownloadPrefix(url);
    setLocalDownload(cleanUrl);
    // Prefix with "download:" so renderer knows to add download attribute
    onChange(cleanUrl ? `download:${cleanUrl}` : '');
  };

  const handleEmailChange = (raw) => {
    // Accept either "user@example.com" or "mailto:user@example.com"
    const clean = raw.replace(/^mailto:/i, '').trim();
    setLocalEmail(clean);
    onChange(clean ? `mailto:${clean}` : '');
  };

  const handlePhoneChange = (raw) => {
    // Accept either "+61412345678" or "tel:+61412345678"
    const clean = raw.replace(/^tel:/i, '').trim();
    setLocalPhone(clean);
    onChange(clean ? `tel:${clean}` : '');
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <label className="text-xs text-slate-500 block">{label}</label>}
      
      {/* Link Type Dropdown */}
      <Select value={selectedType} onValueChange={handleTypeChange}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="Select link type..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No Link</SelectItem>
          <SelectItem value="internal">Internal Page</SelectItem>
          <SelectItem value="external">External URL</SelectItem>
          <SelectItem value="email">Email</SelectItem>
          <SelectItem value="phone">Phone</SelectItem>
          {showAnchor && <SelectItem value="anchor">Anchor</SelectItem>}
          {showDownload && <SelectItem value="download">Download File</SelectItem>}
        </SelectContent>
      </Select>

      {/* Internal Page Selector */}
      {selectedType === 'internal' && (
        <Select value={getCurrentPage()} onValueChange={handlePageSelect}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Select a page..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_none_">-- Select a page --</SelectItem>
            {[...pages]
              .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
              .map((page) => {
                const pathVal = page.full_path || page.slug;
                return (
                  <SelectItem key={page.page_id || page.slug} value={pathVal}>
                    {page.title} (/{pathVal})
                  </SelectItem>
                );
              })}
          </SelectContent>
        </Select>
      )}

      {/* External URL Input */}
      {selectedType === 'external' && (
        <Input
          value={value?.startsWith('http') ? value : localExternal}
          onChange={(e) => handleExternalChange(e.target.value)}
          placeholder={placeholder}
          className="h-8 text-xs"
        />
      )}

      {/* Anchor Selector/Input */}
      {selectedType === 'anchor' && (
        <>
          {anchors && anchors.length > 0 ? (
            <Select value={getCurrentAnchor()} onValueChange={handleAnchorSelect}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select an anchor..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">-- Select an anchor --</SelectItem>
                {anchors.map((anchor, idx) => (
                  <SelectItem key={anchor.id || idx} value={anchor.id}>
                    #{anchor.id} {anchor.name ? `(${anchor.name})` : ''} {anchor.pageSlug ? `- ${anchor.pageSlug}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={value?.startsWith('#') ? value.substring(1) : localAnchor}
              onChange={(e) => handleAnchorInputChange(e.target.value)}
              placeholder="section-name"
              className="h-8 text-xs"
            />
          )}
          <p className="text-xs text-slate-400">
            {anchors && anchors.length > 0 
              ? 'Select an anchor from the list above'
              : 'Enter anchor ID manually (e.g., "contact" for #contact)'}
          </p>
        </>
      )}

      {/* Download File Input */}
      {selectedType === 'download' && (
        <>
          <Input
            value={getCurrentDownload() || localDownload}
            onChange={(e) => handleDownloadChange(e.target.value)}
            placeholder="https://example.com/file.pdf or paste Media Library URL"
            className="h-8 text-xs"
          />
          <p className="text-xs text-slate-400 flex items-center gap-1">
            <Download size={12} />
            Paste a direct URL to a PDF, image, document, etc. Opens as download.
          </p>
        </>
      )}

      {/* Email Input */}
      {selectedType === 'email' && (
        <>
          <Input
            type="email"
            value={localEmail}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="you@example.com"
            className="h-8 text-xs"
            data-testid={`${testId}-email-input`}
          />
          <p className="text-xs text-slate-400 flex items-center gap-1">
            <Mail size={12} />
            Opens the visitor&apos;s email app with a new message to this address.
          </p>
        </>
      )}

      {/* Phone Input */}
      {selectedType === 'phone' && (
        <>
          <Input
            type="tel"
            value={localPhone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="+61 412 345 678"
            className="h-8 text-xs"
            data-testid={`${testId}-phone-input`}
          />
          <p className="text-xs text-slate-400 flex items-center gap-1">
            <Phone size={12} />
            On mobile, tapping the button dials this number. Include the country code (e.g. +61).
          </p>
        </>
      )}

      {/* Preview */}
      {value && (
        <div className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
          {value.startsWith('download:') ? (
            <>
              <Download size={10} className="inline mr-1" />
              Download: <code className="text-indigo-600">{value.substring(9)}</code>
            </>
          ) : value.startsWith('mailto:') ? (
            <>
              <Mail size={10} className="inline mr-1" />
              Email: <code className="text-indigo-600">{value.substring(7)}</code>
            </>
          ) : value.startsWith('tel:') ? (
            <>
              <Phone size={10} className="inline mr-1" />
              Phone: <code className="text-indigo-600">{value.substring(4)}</code>
            </>
          ) : (
            <>URL: <code className="text-indigo-600">{value}</code></>
          )}
        </div>
      )}
    </div>
  );
};

export default LinkSelector;
